import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { FILE_MAX_MB, fileSize } from "./FilePreview";

/** Файл в окне импорта: имя без расширения правится, расширение — как было. */
interface Row {
  key: number;
  file: File;
  base: string;
  ext: string;
  /** Не загрузился (или не загрузится) — почему. */
  error: string | null;
}

/** «выписка.pdf» → «выписка» + «pdf»; у файла без расширения оно пустое. */
function splitName(name: string): { base: string; ext: string } {
  const m = /^(.*)\.([a-z0-9]{1,6})$/i.exec(name);
  return m && m[1] ? { base: m[1], ext: m[2] } : { base: name, ext: "" };
}

const tooBig = (f: File) => f.size > FILE_MAX_MB * 1024 * 1024;

/**
 * Импорт в случай: выбранные файлы — списком, у каждого имя можно поменять
 * до загрузки («IMG_2041» → «ОКТ правого глаза»). Пустое поле — останется
 * исходное имя. Загрузившиеся уходят из списка; не загрузившиеся остаются с
 * причиной — их можно убрать или закрыть окно.
 */
export default function FileImportDialog({
  caseId,
  caseLabel,
  files,
  onClose,
  onDone,
}: {
  caseId: number;
  caseLabel: string;
  files: File[];
  onClose: () => void;
  /** Сколько файлов добавлено; all — загрузились все, окно можно закрывать. */
  onDone: (added: number, all: boolean) => void;
}) {
  const [rows, setRows] = useState<Row[]>(() =>
    files.map((file, key) => ({
      key,
      file,
      ...splitName(file.name),
      error: tooBig(file) ? `Больше ${FILE_MAX_MB} МБ — сожмите скан или разделите документ` : null,
    }))
  );
  const [busy, setBusy] = useState<number | null>(null);
  /** Сколько уже добавлено — тогда «Отмена» становится «Закрыть». */
  const [added, setAdded] = useState(0);
  const first = useRef<HTMLInputElement>(null);

  // Первое имя — сразу выделено: можно просто начать печатать новое.
  useEffect(() => {
    first.current?.select();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && busy === null && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const ready = rows.filter((r) => !tooBig(r.file));
  const update = (key: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  /** Имя для сервера: своё или исходное, с исходным расширением (набранное заново не удваивается). */
  function nameOf(r: Row): string {
    const original = splitName(r.file.name).base;
    let base = r.base.trim() || original;
    if (r.ext && base.toLowerCase().endsWith(`.${r.ext.toLowerCase()}`)) base = base.slice(0, -r.ext.length - 1) || original;
    return r.ext ? `${base}.${r.ext}` : base;
  }

  async function upload() {
    if (busy !== null || !ready.length) return;
    let done = 0;
    let failed = 0;
    for (const r of ready) {
      setBusy(r.key);
      try {
        await api.uploadDocFile(caseId, r.file, nameOf(r));
        done++;
        setRows((prev) => prev.filter((x) => x.key !== r.key));
      } catch (err) {
        failed++;
        update(r.key, { error: (err as Error).message });
      }
    }
    setBusy(null);
    setAdded((n) => n + done);
    onDone(done, failed === 0 && ready.length === rows.length);
  }

  return (
    // Щелчок мимо окна его не закрывает: набранные имена так не теряются.
    <div className="overlay">
      <div className="overlay-frame file-import-frame">
        <div className="close" title="Закрыть" onClick={busy === null ? onClose : undefined} />
        <div className="overlay-header">
          Импорт файлов
          <span className="hist-sub">{caseLabel}</span>
        </div>

        <div className="file-import-list">
          {rows.map((r, i) => (
            <div key={r.key} className={`file-import-row${r.error ? " file-import-row-bad" : ""}`}>
              <div className="file-import-name">
                <input
                  ref={i === 0 ? first : undefined}
                  type="text"
                  value={r.base}
                  placeholder={splitName(r.file.name).base}
                  maxLength={180}
                  disabled={busy !== null || tooBig(r.file)}
                  title={`Исходное имя: ${r.file.name}`}
                  onChange={(e) => update(r.key, { base: e.target.value, error: tooBig(r.file) ? r.error : null })}
                  onKeyDown={(e) => e.key === "Enter" && upload()}
                />
                {r.ext && <span className="file-import-ext">.{r.ext}</span>}
              </div>
              <span className="file-import-size">{busy === r.key ? "загружаем…" : fileSize(r.file.size)}</span>
              <div
                className={`file-import-remove${busy !== null ? " disabled" : ""}`}
                title="Не загружать этот файл"
                onClick={() => busy === null && setRows((prev) => prev.filter((x) => x.key !== r.key))}
              />
              {r.error && <div className="file-import-error">{r.error}</div>}
            </div>
          ))}
          {rows.length === 0 && <div className="file-import-empty">Файлов не осталось.</div>}
        </div>

        <div className="file-import-hint">
          Имя увидят в списке документов случая. Пустое поле — останется исходное имя.
        </div>

        <div className="file-import-buttons">
          <div
            className={`button green${busy !== null || !ready.length ? " disabled" : ""}`}
            onClick={upload}
          >
            {busy !== null ? "Загружаем…" : ready.length > 1 ? `Загрузить (${ready.length})` : "Загрузить"}
          </div>
          <div className={`button${busy !== null ? " disabled" : ""}`} onClick={busy === null ? onClose : undefined}>
            {added ? "Закрыть" : "Отмена"}
          </div>
        </div>
      </div>
    </div>
  );
}
